import * as billingDetailsHelper from './paycentral.billingdetails.helper.js';
import { logger } from "./paycentral.helper.logging.js";
import { ClientManager } from './paycentral.clientmanager.js';

// This class handles the client side logic for PayCentral
// It acts as a adapter between iMIS and the PayCentral SDK

class PayCentralAdapter {

    address;
    containList = [];
    context = {};
    dataVaultJson;
    payload;
    dataVaultResponseToken;
    paymentMethod = {};
    paymentMethodSelector;
    commandButton;
    commandButtonOnClick;
    options = {};
    sdk = null;
    paymentType = null;
    paymentRequest = null;
    container = null;
    overlay = null;
    overlaySeq = 0;
    lastOptionsData = {};
    instanceId = 0;

    /** @private */
    _clientManager;

    /**
     * Constructs a new PayCentralAdapter.
     * @param {Object} initParams - Initialization options.
     * @param {Object} initParams.clientManager - Required ClientManager instance.
     */
    constructor({ clientManager } = {}) {
        if (!clientManager) {
            throw new Error('ClientManager is required for PayCentralAdapter.');
        }
        this._clientManager = clientManager;

        // Catches the event when a user clicks the submit button
        document.addEventListener("beforeSubmit", () => {
            if (!this.submitPayment()) {
                this.fireSubmit();
            }
        }, false);

        window.addEventListener("load", () => {
            // Set any sdk debug flags that may be in the DOM or query string
            this.setFlags();
        });

    }

    // PUBLIC
    // These functions are called from outside this class

    /**
     * Called when the user changes the payment method
     */
    paymentMethodChanged() {
        logger.logVerboseInfo("paycentral.adapter.paymentMethodChanged() was called.");
        this.paymentRequest = null;
        PayCentral.destroy();
        this.clearMessages();
    }

    /**
     * Create pay central ui and add module to window.
     * @param {Object} optionsData - ui options data.
     */
    createUI(optionsData) {

        const instanceId = ++this.instanceId;

        logger.logVerboseInfo("paycentral.adapter.createUI() was called.",
            {
                instanceId: instanceId,
                triggeringControl: optionsData?.triggeringControl ?? "unknown"
            });

        // Tear down any current payment controls.
        // If a control is loading, this will abort its loading operation and reject any promises being awaited.
        PayCentral.destroy();
        this.clearMessages();

        // Now schedule an event to actually create the UI.
        // Using a scheduling approach will allow any awaited promises that were rejected above to complete and tidy up properly.
        window.setTimeout(() => this.handleCreateUI(instanceId, optionsData))

    }

    // Used when the submit button for the paymentcreatordisplay is in the command bar.
    // Pass in the command bar button and the button.onclick event (before we change the event).
    handleCommandButton(button, buttonOnClick) {
        this.commandButton = button;
        this.commandButtonOnClick = this.commandButtonOnClick || buttonOnClick; // Ensure that we don't overwrite the original .onclick event
        this.commandButton.onclick = () => {
            if (this.paymentRequest) {
                // If we have client side validators, fire those before attempting to submit the payment to PayCentral
                if (typeof Page_ClientValidate === "function" && !Page_ClientValidate()) {
                    // have to reset Page_BlockSubmit or it will block the next autopostback control from posting back
                    Page_BlockSubmit = false;
                    return;
                }
                if (!this.submitPayment())
                    this.fireSubmit();
            } else {
                this.fireSubmit();
            }
        };
    }

    // Handles updates to the address
    updateAddress(json) {

        if (json) {
            const address = typeof json === "object" ? json : JSON.parse(json.replace(/\r?\n|\r/g, ' '));
            billingDetailsHelper.updateBillingAddress(address, this.instanceId);
            this.address = address;
            return;
        }

        if (this.address === undefined) {
            // Todo: is this still in use? does anyone know? the only references I see are test references.
            const addressInput = document.querySelector("input[id$='DataVaultAddress']");
            if (addressInput) {
                this.address = addressInput.value;
            }
        }
    }

    // END PUBLIC

    async handleCreateUI(instanceId, optionsData) {

        logger.logVerboseInfo("paycentral.adapter.handleCreateUI() was called.", {instanceId: instanceId});

        if (this.instanceId !== instanceId) {
            logger.logVerboseInfo("paycentral.adapter.handleCreateUI() exited early. Another createUI request has been queued since this one.");
            return;
        }

        this.lastOptionsData = this.deepClone(optionsData);

        this.container = document.querySelector(`div[id$='_${optionsData.moduleTarget}']`);

        try {

            this.sdk = null;
            this.paymentRequest = null;

            // If the container is not visible then do not proceed.
            // PaymentCreator will call createUI even when there are no items in the Cart
            // and portions of the iPart has not been rendered in the browser.
            if (!this.container) return;

            this.paymentType = optionsData.paymentType;
            this.setup();
            if (!this.isPayCentralRequired()) return;
            this.composePayload();

            this.sdk = PayCentral(optionsData.token);

            this.paymentRequest = this.sdk.createPaymentRequest(
                this.buildSdkPaymentRequestPaymentMethod(optionsData.processingFlow, optionsData.region, optionsData.use3ds),
                this.buildSdkPaymentRequestPaymentRequestOptions());

            await this.loadControl(optionsData)
        } catch (e) {
            logger.logError("paycentral.adapter.handleCreateUI() failed with error.", { error: e });
            this.createUIError(e);
        }

    }

    async loadControl(optionsData) {

        const instanceId = this.instanceId;
        const paymentRequest = this.paymentRequest;

        const showOptions = {
            customCss: optionsData?.customCss === true ? true : false
        }
        if (this.showPaymentControlOnError()) showOptions.showOnError = true;

        const disabledElements = this.disableElements(this.getElementsToDisable());

        try {
            await this.paymentRequest.show(this.container, showOptions);
        }
        catch (e) {
            // if the payment method or payment request has changed whilst we were awaiting the control, then ignore any errors
            // the old payment control has already been destroyed
            if (this.instanceId === instanceId
                && this.paymentRequest === paymentRequest) {
                logger.logError("paycentral.adapter.loadControl() failed with an error.", { error: e });
                throw e;
            } else {
                logger.logVerboseInfo("paycentral.adapter.loadControl() failed with an error which we are ignoring as the payment request has changed.", { error: e });
            }
        }
        finally {
            this.restoreDisabledElementsState(disabledElements);
        }

    }

    submitPayment() {

        logger.logVerboseInfo("paycentral.adapter.submitPayment() was called.");

        this.clearMessages();

        if (!this.paymentRequest) return false;

        this.setup();
        if (!this.isProcessingRequired()) return false;

        this.composePayload();

        this.disablePage();

        // Specifically using .then instead of await because of the way submitPayment is called.
        // We do not want everything back up the stack to have to use await.
        // Effectively we are handling this like a callback.

        this.paymentRequest.update(this.buildSdkPaymentRequestPaymentRequestOptions())
            .then(() => this.paymentRequest.submit())
            .then((paymentResult) => this.submitPaymentHandleResult(paymentResult))
            .catch((e) => {
                logger.logError("paycentral.adapter.submitPayment() failed with error.", { error: e });
                this.submitPaymentException(e);
            });

        // tell the caller to do nothing, the callback will trigger the next action
        return true;

    }

    isPayCentralRequired() {
        if (!this.context.UseDatavault
            || !this.context.HasPaymentMethod) return false;
        return true;
    }

    isProcessingRequired() {
        if (!this.isPayCentralRequired()
            || !this.paymentRequest                // if we don't have a paymentRequest then we aren't using the widget
            || !this.context.DoEnrollment
            || this.isBillMeRadioButtonSelected()
            || this.context.HasRequiredFieldErrors) return false;
        return true;
    }

    submitPaymentHandleResult(paymentResult) {

        logger.logVerboseInfo("paycentral.adapter.submitPaymentHandleResult() was called.", { result: paymentResult });

        if (paymentResult.succeeded === true) {
            this.submitPaymentSucceeded(paymentResult);
        } else {
            this.submitPaymentFailed(paymentResult.errors);
        }
    }

    submitPaymentSucceeded(paymentResult) {

        if (paymentResult.capture === "Delayed") {
            this.dataVaultResponseToken.value = JSON.stringify(paymentResult.data);
            this.threeDSCode.value = paymentResult.threeDSCode ?? "";
            this.fireSubmit();
        }
        else if (paymentResult.capture === "Immediate") {
            this.dataVaultCaptureResult.value = JSON.stringify(paymentResult);
            this.threeDSCode.value = paymentResult.threeDSCode ?? "";
            this.fireSubmit();
        } else {
            this.enablePage();
        }

    }

    submitPaymentFailed(errors) {
        this.handleError(errors);
    }

    submitPaymentException(exception) {
        console.log("SDK Response exception: ", exception); //TODO: REMOVE ME

        let msg;
        let isHtml = false;
        if (exception.name === "PayCentral.ComponentUnresponsive") {
            msg = "An error occurred while submitting the payment request.<br>Please reload the page and try again.<br><a href='javascript:location.reload();' class='TextButton'>Reload</a>";
            isHtml = true;
        } else {
            msg = "An error occurred while submitting the payment request.";
            if (exception?.message)
                msg += `\r\n${exception.message}`;
            else if (exception)
                msg += `\r\n${exception}`;
        }
        this.handleError(msg, isHtml);
    }

    // Immediately after client click and before posting to DV, 
    // grab all necessary inputs + build the selected payment method + set the context to be used to decided whether we post to DV or not
    setup() {
        this.paymentMethodSelector = document.querySelector("select[id$='_PaymentMethodDropDown']") || document.querySelector("select[id$='PaymentMethodSelectBox']");
        this.dataVaultResponseToken = document.querySelector("input[id$='DataVaultResponseToken']"); // Input populated when we receive a successful token from DV
        this.dataVaultJson = document.querySelector("input[id$='DataVaultJson']"); // Input populated with the json to be passed to DV   
        this.threeDSCode = document.querySelector("input[id$='ThreeDSCode']");
        this.dataVaultCaptureResult = document.querySelector("input[id$='DataVaultCaptureResult']");
        this.setContext();
    }

    // Compose the payload by grabbing values supplied in the dataVaultJson element and updating them with user input changes
    composePayload() {
        const obj = this.getDataVaultJsonElementValue();

        // Refresh billing details.
        if (obj.Address) {
            billingDetailsHelper.setBillingDetails(obj.Address, obj.Name, this.instanceId);
        }
        const billingDetails = billingDetailsHelper.getBillingDetails();
        // If credit card payment replace Full Name with Cardholder name.
        if (this.paymentType === "CreditCard") {
            const cardholderNameFromInput = this.getCardholderNameFromInputField();
            if (cardholderNameFromInput)
                billingDetails.Name.FullName = cardholderNameFromInput;
        }


        const payload = {
            DvGatewayID: this.paymentMethod.GatewayId,
            GatewayProvider: this.paymentMethod.GatewayName.toUpperCase(),
            // A user opting in via the "Save payment details for use in future purchases" checkbox
            // needs the same off-session/multi-use mandate treatment as a genuine AutoPay enrolment
            // (IsRecurring) - both ultimately map to SetupFutureUsage on the gateway side. OR them
            // here so the flag reaches the gateway *before* pre-auth, matching how AutoPay already works.
            IsRecurring: this.getIsRecurring() || this.getSaveForFutureUse(),
            AccountType: this.getBankAccountType(),
            Amount: this.getAmount(),
            Currency: this.paymentMethod.Currency.toUpperCase(),
            Name: billingDetails.Name,
            Address: billingDetails.Address,
            PayCentralUiUrl: this.PayCentralUiUrl,                     //TODO: Remove this??
            supports3DS: { supports3Ds: false, utilize3Ds: false } //TODO: Remove this??
        };


        payload.paymentRequestOptions = this.options;
        payload.paymentRequestData = this.getPaymentRequestData();

        this.payload = payload;
    }

    // Gateway provider setting to request 3DS authentication.
    supports3DS(currency, provider) {
        let supports3Ds = false;
        let utilize3Ds = false;

        if (provider === "GLOBAL PAYMENTS") {
            supports3Ds = true;
            utilize3Ds = (currency === "EUR" || currency === "GBP");
        }

        return { supports3Ds, utilize3Ds };
    }

    // Displays messages received from DV
    showMessages(msg, isHtml) {
        if (msg == null) {
            console.log("No error message(s) to show from DV");
            return;
        }

        let errors;
        if (isHtml)
            errors = [msg];
        else if (isArray(msg))
            errors = [...msg];
        else if (typeof msg === "string" && msg)
            errors = msg.split('#');
        if (!errors) errors = ["Unknown error"];

        const messagecontainer = this.getMessageContainer();
        if (!messagecontainer) {
            console.warn("Could not display the following PayCentral messages as the message container is not visible.");
            console.warn(errors);
            return;
        }

        const div = document.createElement("div");
        div.style.marginBottom = "1em";

        errors.forEach((x) => {
            let span = document.createElement("span");
            if (isHtml)
                span.innerHTML = x;
            else
                span.textContent = x;
            span.setAttribute("style", "display:block;");
            span.setAttribute("class", "Important ValidationError");
            div.appendChild(span);
        });

        messagecontainer.appendChild(div);

    }

    getMessageContainer() {
        return this.container?.querySelector(`span[id=PayCentralUserMessages]`);
    }

    clearMessages() {
        const messagecontainer = this.getMessageContainer();
        if (!messagecontainer) return;
        while (messagecontainer.lastElementChild) {
            messagecontainer.lastElementChild.remove();
        }
    }

    // Checks that all required inputs have been filled in - note this checks all controls with the aria-required attribute that exist in the
    // iMIS code not the iframe - this turns out (at the time of writing) to be simply the credit card name field - the effect of this check
    // failing is to not send any data to PayCentral 
    hasRequiredFieldErrors() {
        let ret = false;
        let creditCardPanelVisible = jQuery('#PayCentralDiv').find("div[id$='_CreditCardPanel']").is(':visible');
        let bankDraftPanelVisible = jQuery('#PayCentralDiv').find("div[id$='_BankDraftPanel']").is(':visible');

        if (creditCardPanelVisible) {
            let creditCardPanel = jQuery('#PayCentralDiv').find("div[id$='_CreditCardPanel']");
            let requiredElements = creditCardPanel.find('input[aria-required]');
            requiredElements.each(function (_x, y) {
                if (!y.value) {
                    ret = true;
                    return false;
                }
            });
            return ret;
        }

        if (bankDraftPanelVisible) {
            let bankDraftPanel = jQuery('#PayCentralDiv').find("div[id$='_BankDraftPanel']");
            let requiredElements = bankDraftPanel.find('input[aria-required]');
            requiredElements.each(function (_x, y) {
                if (!y.value) {
                    ret = true;
                    return false;
                }
            });
            return ret;
        }

        return ret;
    }

    // Used when tokenization fails, ensures the submit button will be re-enabled
    enableSubmitButton() {
        let saveButton = document.querySelector("input[ID$='_SaveButton']") || document.querySelector("input[ID$='_SubmitCartOrderButton']");
        if (saveButton) {
            saveButton.disabled = false;
        }
    }

    // Use context to determine whether we should post to DataVault
    setContext() {
        this.context.HasPaymentMethod = this.hasSelectedPaymentMethod(this.paymentMethodSelector);
        this.context.HasRequiredFieldErrors = this.hasRequiredFieldErrors();
        this.context.HasUpdatedAddress = this.address !== undefined;
        this.context.UseDatavault = this.paymentMethod.GatewayId !== null;
        this.context.DoEnrollment = this.handleEnrollmentSubmit();
    }

    // Create a payment method object of the currently selected payment method
    hasSelectedPaymentMethod(select) {
        if (!select) return false;
        let selectedOption = select.options[select.selectedIndex];
        this.paymentMethod.GatewayId = selectedOption.getAttribute("data-DataVaultGatewayAccountId"); // ID associated with the gateway
        this.paymentMethod.GatewayName = selectedOption.getAttribute("data-DataVaultAuthorizationGateway"); // Name of the gateway
        this.paymentMethod.Currency = selectedOption.getAttribute("data-currencycode"); // Payment method Currency code

        return true;
    }

    isBillMeRadioButtonSelected() {
        const selected = document.querySelector("input[type='radio'][name$='PaymentMethodRadioButtonList'][value='BillMe']:checked");
        return !!selected;
    }

    /**
     * @desc handle on-error action.
     * @param {any} message
     */
    handleError(message, isHtml) {
        this.enablePage();
        this.showMessages(message, isHtml);
        this.enableSubmitButton();
    }

    // Fires the submit button 
    fireSubmit() {
        if (this.commandButton && this.commandButtonOnClick) { // Click the command bar button with original event
            this.commandButton.onclick = this.commandButtonOnClick;
            this.commandButton.click();
        }
        else {
            let event = new CustomEvent("doSubmitPayCentral", { bubbles: true });
            document.dispatchEvent(event);
        }
    }

    // Determines when we should post to DV when managing enrollments
    handleEnrollmentSubmit() {
        let status = document.querySelector("select[id$='_ListDonationStatus']") || document.querySelector("span[id$='_LabelDonationStatusData']");
        if (!status) return true;

        if (status.tagName.toUpperCase() != 'SPAN') {
            let selectedValue = "";
            if (status.selectedOptions === undefined) {
                selectedValue = status[status.selectedIndex].value;
            }
            else {
                selectedValue = status.selectedOptions[0].value;
            }

            if (selectedValue === "Canceled") return false;
        }

        let selectedOption = "";
        let option = document.querySelector("select[id$='_ListDonationPaymentOption']");
        if (!option) return true;

        if (option.selectedOptions === undefined) {
            selectedOption = option[option.selectedIndex].value;
        }
        else {
            selectedOption = option.selectedOptions[0].value;
        }
        if (selectedOption !== "new") return false;
        return true;
    }

    /**
     * Returns the selected payment instrument and its associated settings
     * (pulled from the data-attributes attached to the selected dropdown list option).
     *
     * @returns {PaymentInstrument} paymentInstrument - A payment instrument object.
     * @returns {string} paymentInstrument.Id - Gateway provider id.
     * @returns {string} paymentInstrument.GatewayName - Gateway provider name.
     * @returns {string} paymentInstrument.Currency - Currency code attached to selected payment method.
     * @returns {string} paymentInstrument.PaymentMethodType - Payment method type: ["creditcard", "bankdraft"].
     * @returns {boolean} paymentInstrument.RequireThreeDs - Require 3DS Secure Customer Authentication (SCA) [default: false].
     */
    getSelectedPaymentInstrumentData() {
        const paymentMethodSelectorElement = document.querySelector("select[id$='_PaymentMethodDropDown']") || document.querySelector("select[id$='PaymentMethodSelectBox']");
        if (!paymentMethodSelectorElement || !paymentMethodSelectorElement.options[paymentMethodSelectorElement.selectedIndex]) return {};

        const pm = paymentMethodSelectorElement.options[paymentMethodSelectorElement.selectedIndex];
        let paymentInstrument = {
            ProviderId: pm.getAttribute("data-DataVaultGatewayAccountId"),
            ProviderType: pm.getAttribute("data-DataVaultAuthorizationGateway").toUpperCase(),
            Currency: pm.getAttribute("data-currencycode"),
            Name: pm.getAttribute("data-DataVaultAuthorizationGateway").toUpperCase(),
            Type: pm.value.split("^")[0]
        };

        const { supports3Ds, utilize3Ds } = this.supports3DS(paymentInstrument.Currency, paymentInstrument.Provider);
        paymentInstrument.Supports3Ds = supports3Ds;
        paymentInstrument.Utilize3Ds = utilize3Ds;

        return paymentInstrument;
    }

    /**
     * This data comes from the PayCentralFrame control. It is added to the indicated html input element as a json data string.
     *
     * @returns {Object} A payload object.
     */
    getDataVaultJsonElementValue() {

        if (!this.dataVaultJson || !this.dataVaultJson.value) return {};

        // the data might not quite have finished loading
        let attempt = 10;
        while (attempt > 0) {
            try {
                return JSON.parse(this.dataVaultJson.value);
            }
            catch (_) { }
            attempt--;
        }

        return {};

    }

    /**
     * Returns the cart, invoice or donation amount value.
     *
     * @returns {string} A amount value.
     */
    getAmount() {
        if (!this._clientManager) {
            logger.logError('paycentral.adapter.getAmount() failed with error.', { error: 'ClientManager is not initialized.' });
            return null;
        }

        const amount = this._clientManager.getAmount();
        if (amount == null) {
            logger.logError('paycentral.adapter.getAmount() failed with error.', { error: 'No valid amount available.' });
        }
        return amount;     
    }

    getBankAccountType() {
        // Set bank account - "account type" (checking/savings).
        if (this.paymentType === "DirectDebit") {
            const accountTypeDropdown = document.querySelector("select[id$='BankAccountTypeDropDown']");
            if (accountTypeDropdown !== null && accountTypeDropdown !== 'undefined') {
                return document.querySelector("select[id$='BankAccountTypeDropDown']").value;
            } else {
                return "Checking";
            }
        } else {
            return null;
        }
    }

    getIsRecurring() {
        // Set is recurring indicator.
        let isRecurringElement = document.querySelector("[id$='IsRecurring']");
        if (isRecurringElement) {
            return this.convertToBool(isRecurringElement.value);
        }
        else {
            return null;
        }
    }

    getSaveForFutureUse() {
        // Live DOM state of the "Save payment details for use in future purchases" checkbox
        // (SavedPaymentOptInCheckbox). Unlike IsRecurring, this is a user-interactive checkbox
        // (AutoPostBack="false"), so its checked state must be read directly from the DOM at the
        // moment the payload is composed - there is no server-rendered hidden field mirroring it.
        // Read here (same timing as getIsRecurring()) so the widget can flag SetupFutureUsage=true
        // to the gateway *before* pre-auth, the same way genuine AutoPay already does.
        let saveForFutureUseElement = document.querySelector("[id$='SavedPaymentOptInCheckbox']");
        if (saveForFutureUseElement) {
            return !!saveForFutureUseElement.checked;
        }
        else {
            return false;
        }
    }

    getCardholderNameFromInputField() {
        const cardholderNameElement = document.querySelector("input[id$='txtCCName']");
        if (cardholderNameElement)
            return cardholderNameElement.value;
        return null;
    }

    // Helper method - Used by .getIsRecurring().
    convertToBool(s) {
        if (typeof s == "boolean") return s;
        if (typeof s != "string") return undefined;
        if (s.toLowerCase() === "true") return true;
        if (s.toLowerCase() === "false") return false;
        return undefined;
    }

    /**
    * Retrieves the method data, transaction details, and payer information collected during the checkout process.
    * 
    * @returns {object} A PaymentRequestData object.
    */
    getPaymentRequestData() {
        const selectedMethod = this.getSelectedPaymentInstrumentData();
        const amount = this.getAmount();

        // Build legacy PayCentral UI payload.
        const billingDetails = billingDetailsHelper.getBillingDetails();
        const payer = {
            BillingAddress: billingDetails.Address,
            FirstName: billingDetails.Name.FirstName,
            LastName: billingDetails.Name.LastName,
            FullName: billingDetails.Name.FullName,
            CardholderName: this.getCardholderNameFromInputField()
        }

        console.log("Build legacy PayCentral UI payload", JSON.stringify(billingDetails)); //Todo: REMOVE ME

        return {
            PaymentInstrument: {
                Card: {
                    ContactName: payer.CardholderName,
                    Address: payer.BillingAddress
                },
                MethodData: {
                    Name: selectedMethod.Name,
                    Type: selectedMethod.Type,
                    GatewayId: selectedMethod.ProviderId,
                    GatewayProvider: selectedMethod.ProviderType,
                    Supports3Ds: selectedMethod.Supports3Ds
                },
                RetainOnSuccess: true
            },
            TransactionDetails: {
                Payer: payer,
                PayerEmail: null,
                PayerPhone: null,
                PaymentInstrument: {
                    Card: {
                        ContactName: payer.CardholderName,
                        Address: payer.BillingAddress
                    },
                    MethodData: {
                        Name: selectedMethod.Name,
                        Type: selectedMethod.Type,
                        GatewayId: selectedMethod.ProviderId,
                        GatewayProvider: selectedMethod.ProviderType,
                        Supports3Ds: selectedMethod.Supports3Ds
                    },
                    RetainOnSuccess: true
                },
                Total: {
                    Amount: amount,
                    Currency: {
                        CurrencyCode: selectedMethod.Currency
                    }
                },
                // The more data the better - we may want to include additional order information from the paymentRequest SDK in the future.
                Order: {
                    OrderId: null,
                    OrderTotal: {
                        Amount: amount,
                        Currency: { CurrencyCode: selectedMethod.Currency }
                    },
                    ProcessingModel: null,
                    ShippingAddress: {}
                }
            },
            Merchant: {
                TenantId: null,
                MerchantOrigin: window.location.origin
            },
            Options: {
                CaptureMode: null,
                //Country: "pull from DV side",
                PurchaseFlow: this.options.ProcessingFlow,
                Recurring: false, //hardcoded for now - pull from Brian's work.
                Request3DS: true
            }
        };
    }

    createUIError(e) {
        let msg = "Could not load the payment control.";
        if (e?.message)
            msg += `\r\n${e.message}`;
        else
            msg += `\r\n${e}`;
        this.handleError(msg);
    }

    buildSdkPaymentRequestPaymentMethod(processingFlow, region, use3ds) {

        const prd = this.payload.paymentRequestData;

        const paymentMethod = {
            paymentType: this.paymentType,
            capture: processingFlow === "Tokenize" ? "Delayed" : "Immediate",
            gateway: {
                id: prd.PaymentInstrument.MethodData.GatewayId,
                provider: prd.PaymentInstrument.MethodData.GatewayProvider
            }
        };

        if (use3ds === true) {
            paymentMethod.gateway.options = paymentMethod.gateway.options || {};
            paymentMethod.gateway.options.use3ds = true;
        }

        if (region) paymentMethod.region = region;

        return paymentMethod;

    }

    buildSdkPaymentRequestPaymentRequestOptions() {

        const prd = this.payload.paymentRequestData;

        const paymentRequestOptions = {
            amount: {
                amount: this.getAmount(),
                currency: prd.TransactionDetails.Total.Currency.CurrencyCode
            },
            billingDetails: {
                name: {
                    firstName: this.payload.Name.FirstName,
                    lastName: this.payload.Name.LastName
                },
                address: {
                    addressLines: [...(prd.TransactionDetails.Payer.BillingAddress.AddressLines ?? [])],
                    cityName: prd.TransactionDetails.Payer.BillingAddress.CityName,
                    countryCode: prd.TransactionDetails.Payer.BillingAddress.CountryCode,
                    countryName: prd.TransactionDetails.Payer.BillingAddress.CountryName,
                    countrySubEntityCode: prd.TransactionDetails.Payer.BillingAddress.CountrySubEntityCode,
                    countrySubEntityName: prd.TransactionDetails.Payer.BillingAddress.CountrySubEntityName,
                    postalCode: prd.TransactionDetails.Payer.BillingAddress.PostalCode
                }
            },
            recurring: this.convertToBool(this.payload.IsRecurring) ?? false
        };

        if (this.paymentType === "CreditCard") {
            paymentRequestOptions.card = {
                // if there is no cardholder name field on the page (like in cash payments in v100) 
                // then default to using FullName 
                cardholderName: prd.TransactionDetails.Payer.CardholderName === null
                    ? this.payload.Name.FullName ?? null
                    : prd.TransactionDetails.Payer.CardholderName
            };
        }

        if (this.paymentType === "DirectDebit") {
            paymentRequestOptions.account = {
                accountType: this.payload.AccountType ?? null
            }
        }

        console.log(`[${this.instanceId}]`, "SDK PaymentRequestOptions", JSON.stringify(paymentRequestOptions)); //Todo: REMOVE ME

        return paymentRequestOptions;
    }

    showPaymentControlOnError() {
        try {
            return [...(new URLSearchParams(document.location.search)).keys()]
                .some(x => x.toLowerCase() === "showpaymentcontrolonerror");
        } catch (e) {
            return false;
        }
    }

    useDisableCheckout() {
        return jQuery("#DataVaultDisableCheckout")?.val()?.toLowerCase() === 'true';
    }

    /**
      * Disable the page whilst a payment is being processed
      */
    disablePage() {
        if (!this.useDisableCheckout()) return;
        this.createOverlay();
    }

    /**
     * Enable the page after payment processing has completed, or an error needs to be displayed
     */
    enablePage() {
        if (!this.useDisableCheckout()) return;
        this.destroyOverlay();
    }

    createOverlay() {

        this.destroyOverlay();

        // Create modal dialog and all children elements 
        const processingPaymentMessage = jQuery("#PaymentProcessingMessage").val();
        const element = document.createElement("div");
        element.id = `paymentProcessingStatus_${this.overlaySeq++}`;
        element.className = "modal fade";
        element.setAttribute("role", "status");
        element.innerHTML = '<div class="modal-dialog modal-dialog-status modal-dialog-centered"><div class="modal-content">'
            + '<div class="modal-body"><span class="spinner-dark"></span><h4>' + processingPaymentMessage + '</h4></div>'
            + '</div></div>';
        document.body.appendChild(element);

        // if a 3DS challenge UI kicks in then hide the overlay message
        // when the 3DS challenge goes away, show the message again 
        const overlayHandler = (mutationList, observer) => {
            for (const mutation of mutationList) {
                if (mutation.type === 'childList') {
                    if (Array.from(mutation.addedNodes).some(is3dsOverlay)) {
                        this.hideOverlay();
                        return;
                    }
                    if (Array.from(mutation.removedNodes).some(is3dsOverlay)) {
                        this.showOverlay();
                        return;
                    }
                }
            }
            function is3dsOverlay(e) {
                return e.tagName.toLowerCase() === "div" && e.id.startsWith("PayCentral-overlay-");
            }
        };

        this.overlay = {
            element: element,
            observer: new MutationObserver(overlayHandler)
        };

        this.showOverlay();
        this.overlay.observer.observe(document.body, { childList: true });

    }

    showOverlay() {
        if (!this.overlay?.element) return;
        jQuery(this.overlay.element).modal({ backdrop: 'static', keyboard: false }, 'show');
    }

    hideOverlay() {
        if (!this.overlay?.element) return;
        jQuery(this.overlay.element).modal('hide');
    }

    destroyOverlay() {
        if (this.overlay?.element) {
            jQuery(this.overlay.element).modal('hide');
            this.overlay.element.remove();
        }
        if (this.overlay?.observer) {
            this.overlay.observer.disconnect();
        }
        this.overlay = null;
    }

    setFlags() {
        if (!window.PayCentral) return;
        const prefix = "paycentral.sdk.flags.";
        const usp = new URLSearchParams(document.location.search);
        const keys = [...usp.keys()].filter(k => k.startsWith(prefix));
        if (keys.length) {
            for (const key of keys) {
                const values = usp.getAll(key);
                const flag = key.substring(prefix.length);
                this.setFlag(flag, values.length > 0 ? values[0] : "");
            }
        }
        // special case for verboseLogging which has a SystemConfig setting
        this.setFlag("verboseLogging", logger.verboseLogging);
    }

    setFlag(flag, value) {
        try {
            if (typeof value === "string") {
                if (value.toLowerCase() === "true")
                    value = true;
                else if (value.toLowerCase() === "false")
                    value = false;
            }
            PayCentral.flags[flag] = value;
        } catch (e) { }
    }

    deepClone(o) {
        try {
            if ("structuredClone" in window && typeof window["structuredClone"] === "function") {
                return window["structuredClone"](o);
            }
        } catch (e) { }
        try {
            return JSON.parse(JSON.stringify(o));
        } catch (e) { }
        // it won't clone, unlikely
        return o;
    }

    getElementsToDisable() {
        const elements = [];

        // elements specifically decorated with the  data-paycentral-sdk-disable='true'  attribute
        elements.push(...document.querySelectorAll("[data-paycentral-sdk-disable='true' i]"));
        // a save button used for submitting a donation
        elements.push(...document.querySelectorAll("input[type=button][id$='savebutton' i][data-button-purpose='SubmitDonation' i]"));

        return elements;
    }

    disableElements(elements) {
        if (!(elements instanceof NodeList || Array.isArray(elements))
            || elements.length === 0) return;
        const disabledElements = [];
        for (const element of elements) {
            try {
                var x = {
                    element: element,
                    oldDisabled: element.disabled
                };
                element.disabled = true;
                disabledElements.push(x);
            }
            catch (_) { }
        }
        return disabledElements;
    }

    restoreDisabledElementsState(disabledElements) {
        if (!Array.isArray(disabledElements) || disabledElements.length === 0) return;
        for (const de of disabledElements) {
            try {
                de.element.disabled = de.oldDisabled;
            }
            catch (_) { }
        }
    }    
}

window.Asi = window.Asi || {};
window.Asi.PayCentral = window.Asi.PayCentral || {};

// Ensure the global ClientManager is initialized before constructing PayCentralManager.
// Each user control is responsible for setting up the delegate functions prior to usage.
if (!window.Asi.PayCentral.ClientManager) {
    window.Asi.PayCentral.ClientManager = new ClientManager();
}

try {
    const clientManager = window.Asi.PayCentral.ClientManager;
    window.PayCentralAdapter = new PayCentralAdapter({ clientManager });
} catch (err) {
    logger.logError('PayCentralManager initialization failed.', { error: err });
}